import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Star, X } from "lucide-react";

export default function RatingSheet({ ride, onClose, onRated }) {
  const { t } = useTranslation();
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!stars) return toast.error(t("rating.pickStars"));
    setSaving(true);
    try {
      await api.post(`/rides/${ride.id}/rate`, { rating: stars, comment: comment.trim() || null });
      toast.success(t("rating.thanks"));
      onRated && onRated();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const labels = ["", t("rating.l1"), t("rating.l2"), t("rating.l3"), t("rating.l4"), t("rating.l5")];
  const shown = hover || stars;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1000] bg-[#1A1A1A]/50 backdrop-blur-sm flex items-end sm:items-center sm:justify-center"
      onClick={onClose}
      data-testid="rating-overlay"
    >
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="bg-[#FDF8F0] w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 pb-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="rating-modal"
      >
        <div className="flex justify-between items-start">
          <div>
            <h2 className="font-head font-black text-2xl tracking-tight text-[#1A1A1A]">{t("rating.title")}</h2>
            <p className="mt-1 text-sm text-[#75807A]">
              {t("rating.subtitle")} {ride.driver_name ? `· ${ride.driver_name}` : ""}
            </p>
          </div>
          <button data-testid="rating-close" onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-[#F0EBE1] flex items-center justify-center">
            <X className="w-5 h-5 text-[#75807A]" />
          </button>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              data-testid={`star-${s}`}
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setStars(s)}
              className="transition-transform hover:scale-110 active:scale-95"
            >
              <Star className={`w-11 h-11 ${shown >= s ? "fill-[#F26419] text-[#F26419]" : "text-[#E2DFD8]"}`} strokeWidth={1.5} />
            </button>
          ))}
        </div>
        <p className="mt-3 text-center text-sm font-semibold text-[#D94F2B] h-5">{labels[shown] || ""}</p>

        <textarea
          data-testid="rating-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("rating.commentPh")}
          rows={3}
          className="mt-4 w-full rounded-xl border-2 border-[#E2DFD8] p-4 text-sm focus:outline-none focus:border-[#D94F2B] resize-none"
        />

        <button
          data-testid="rating-submit"
          onClick={submit}
          disabled={saving}
          className="mt-4 w-full h-14 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-lg disabled:opacity-60 transition-transform hover:-translate-y-0.5"
        >
          {saving ? t("common.loading") : t("rating.submit")}
        </button>
        <button data-testid="rating-skip" onClick={onClose} className="mt-2 w-full h-11 text-sm text-[#75807A] font-medium">
          {t("rating.skip")}
        </button>
      </motion.div>
    </motion.div>
  );
}
